import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { formatPhone, formatDate } from '../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  Plus,
  MagnifyingGlass,
  Funnel,
  Upload,
  Trash,
  Queue,
  CaretDown,
  CaretUp,
  Phone
} from '@phosphor-icons/react';

const STATUS_OPTIONS = ['new', 'queued', 'calling', 'completed', 'failed', 'no_answer', 'callback_scheduled'];
const SOURCE_OPTIONS = ['webhook', 'form', 'manual', 'meta_lead_ad', 'csv_import'];
const PRIORITY_OPTIONS = ['high', 'medium', 'low'];

function StatusBadge({ status }) {
  const styles = {
    new: 'badge-info',
    queued: 'badge-warning',
    calling: 'bg-yellow-500 text-black animate-pulse',
    completed: 'badge-success',
    failed: 'badge-error',
    no_answer: 'badge-neutral',
    callback_scheduled: 'badge-warning'
  };
  return (
    <span className={`badge ${styles[status] || 'badge-neutral'}`}>
      {status?.replace('_', ' ')}
    </span>
  );
}

function PriorityBadge({ priority }) {
  const styles = {
    high: 'badge-error',
    medium: 'badge-warning',
    low: 'badge-neutral'
  };
  return (
    <span className={`badge ${styles[priority] || 'badge-neutral'}`}>
      {priority}
    </span>
  );
}

export default function Leads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    source: '',
    priority: '',
    search: '',
    sortBy: 'createdAt',
    sortDir: 'desc'
  });

  const loadLeads = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        sortBy: filters.sortBy,
        sortDir: filters.sortDir
      });
      if (filters.status) params.set('status', filters.status);
      if (filters.source) params.set('source', filters.source);
      if (filters.priority) params.set('priority', filters.priority);
      if (filters.search) params.set('search', filters.search);

      const response = await api.get(`/leads?${params}`);
      setLeads(response.data.leads);
      setPagination(prev => ({ ...prev, ...response.data.pagination }));
    } catch (error) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const handleSort = (field) => {
    setFilters(prev => ({
      ...prev,
      sortBy: field,
      sortDir: prev.sortBy === field && prev.sortDir === 'desc' ? 'asc' : 'desc'
    }));
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedLeads(leads.map(l => l.id));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleSelectLead = (id, checked) => {
    if (checked) {
      setSelectedLeads(prev => [...prev, id]);
    } else {
      setSelectedLeads(prev => prev.filter(lid => lid !== id));
    }
  };

  const handleBulkAction = async (action, value) => {
    if (selectedLeads.length === 0) {
      toast.error('No leads selected');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('action', action);
      formData.append('leadIds', selectedLeads.join(','));
      if (value) formData.append('value', value);

      await api.post('/leads/bulk-action', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(`${selectedLeads.length} leads updated`);
      setSelectedLeads([]);
      loadLeads();
    } catch (error) {
      toast.error('Bulk action failed');
    }
  };

  const SortIcon = ({ field }) => {
    if (filters.sortBy !== field) return null;
    return filters.sortDir === 'desc' ? <CaretDown size={14} /> : <CaretUp size={14} />;
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-medium text-zinc-50 tracking-tight">Leads</h1>
          <p className="text-zinc-400 mt-1">{pagination.total} total leads</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowImportModal(true)}
            className="btn-secondary flex items-center gap-2"
            data-testid="import-csv-btn"
          >
            <Upload size={18} /> Import CSV
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center gap-2"
            data-testid="add-lead-btn"
          >
            <Plus size={18} /> Add Lead
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              placeholder="Search leads..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="input-field pl-10"
              data-testid="search-input"
            />
          </div>

          {/* Status filter */}
          <Select value={filters.status || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v === "all" ? "" : v }))}>
            <SelectTrigger className="w-[150px] bg-zinc-900 border-zinc-800" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUS_OPTIONS.map(s => (
                <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Source filter */}
          <Select value={filters.source || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, source: v === "all" ? "" : v }))}>
            <SelectTrigger className="w-[150px] bg-zinc-900 border-zinc-800" data-testid="source-filter">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              {SOURCE_OPTIONS.map(s => (
                <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Priority filter */}
          <Select value={filters.priority || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, priority: v === "all" ? "" : v }))}>
            <SelectTrigger className="w-[130px] bg-zinc-900 border-zinc-800" data-testid="priority-filter">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {PRIORITY_OPTIONS.map(p => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Bulk Actions */}
        {selectedLeads.length > 0 && (
          <div className="flex items-center gap-4 mt-4 pt-4 border-t border-zinc-800">
            <span className="text-sm text-zinc-400">{selectedLeads.length} selected</span>
            <button
              onClick={() => handleBulkAction('queue')}
              className="btn-secondary text-sm py-1 flex items-center gap-1"
              data-testid="bulk-queue-btn"
            >
              <Queue size={16} /> Queue for Calling
            </button>
            <button
              onClick={() => handleBulkAction('priority', 'high')}
              className="btn-secondary text-sm py-1"
              data-testid="bulk-priority-btn"
            >
              Set High Priority
            </button>
            <button
              onClick={() => handleBulkAction('delete')}
              className="btn-secondary text-sm py-1 text-red-400 hover:text-red-300"
              data-testid="bulk-delete-btn"
            >
              <Trash size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="card p-0">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th className="w-10">
                  <Checkbox
                    checked={selectedLeads.length === leads.length && leads.length > 0}
                    onCheckedChange={handleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th className="cursor-pointer" onClick={() => handleSort('firstName')}>
                  <div className="flex items-center gap-1">
                    Name <SortIcon field="firstName" />
                  </div>
                </th>
                <th>Phone</th>
                <th>Company</th>
                <th>Source</th>
                <th>Status</th>
                <th>Priority</th>
                <th className="cursor-pointer" onClick={() => handleSort('createdAt')}>
                  <div className="flex items-center gap-1">
                    Created <SortIcon field="createdAt" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-12 text-zinc-500">Loading...</td>
                </tr>
              ) : leads.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-12 text-zinc-500">
                    No leads found. Add your first lead to get started.
                  </td>
                </tr>
              ) : (
                leads.map((lead) => (
                  <tr
                    key={lead.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/dashboard/leads/${lead.id}`)}
                    data-testid={`lead-row-${lead.id}`}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedLeads.includes(lead.id)}
                        onCheckedChange={(checked) => handleSelectLead(lead.id, checked)}
                      />
                    </td>
                    <td className="font-medium text-zinc-50">
                      {lead.firstName} {lead.lastName}
                    </td>
                    <td className="font-mono text-zinc-400">{formatPhone(lead.phone)}</td>
                    <td className="text-zinc-400">{lead.company || '-'}</td>
                    <td className="text-zinc-400 capitalize">{lead.source?.replace('_', ' ')}</td>
                    <td><StatusBadge status={lead.status} /></td>
                    <td><PriorityBadge priority={lead.priority} /></td>
                    <td className="text-zinc-500">{formatDate(lead.createdAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-800">
            <div className="text-sm text-zinc-500">
              Page {pagination.page} of {pagination.pages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="btn-secondary text-sm py-1 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.pages}
                className="btn-secondary text-sm py-1 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Lead Modal */}
      <AddLeadModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          setShowAddModal(false);
          loadLeads();
        }}
      />

      {/* Import CSV Modal */}
      <ImportCsvModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={() => {
          setShowImportModal(false);
          loadLeads();
        }}
      />
    </div>
  );
}

function AddLeadModal({ open, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    company: '',
    priority: 'medium',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/leads', formData);
      toast.success('Lead created successfully');
      setFormData({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        company: '',
        priority: 'medium',
        notes: ''
      });
      onSuccess();
    } catch (error) {
      toast.error('Failed to create lead');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md" data-testid="add-lead-modal">
        <DialogHeader>
          <DialogTitle className="text-zinc-50">Add New Lead</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400">First Name *</Label>
              <Input
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
                required
                data-testid="add-lead-firstname"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Last Name</Label>
              <Input
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
                data-testid="add-lead-lastname"
              />
            </div>
          </div>
          <div>
            <Label className="text-zinc-400">Phone Number *</Label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="bg-zinc-800 border-zinc-700"
              placeholder="+1 (555) 123-4567"
              required
              data-testid="add-lead-phone"
            />
          </div>
          <div>
            <Label className="text-zinc-400">Email</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="bg-zinc-800 border-zinc-700"
              data-testid="add-lead-email"
            />
          </div>
          <div>
            <Label className="text-zinc-400">Company</Label>
            <Input
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              className="bg-zinc-800 border-zinc-700"
              data-testid="add-lead-company"
            />
          </div>
          <div>
            <Label className="text-zinc-400">Priority</Label>
            <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="add-lead-priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_OPTIONS.map(p => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="border-zinc-700">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-500" data-testid="add-lead-submit">
              {loading ? 'Creating...' : 'Create Lead'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ImportCsvModal({ open, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/leads/import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(`Imported ${response.data.imported} leads`);
      setFile(null);
      onSuccess();
    } catch (error) {
      toast.error('Failed to import CSV');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md" data-testid="import-csv-modal">
        <DialogHeader>
          <DialogTitle className="text-zinc-50">Import Leads from CSV</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-zinc-400">CSV File</Label>
            <p className="text-xs text-zinc-500 mb-2">
              Required columns: firstName, phone. Optional: lastName, email, company, priority, notes
            </p>
            <Input
              type="file"
              accept=".csv"
              onChange={(e) => setFile(e.target.files[0])}
              className="bg-zinc-800 border-zinc-700"
              data-testid="csv-file-input"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="border-zinc-700">
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !file} className="bg-blue-600 hover:bg-blue-500" data-testid="import-csv-submit">
              {loading ? 'Importing...' : 'Import'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
